import React, { useState, useEffect, useCallback } from 'react'
import contentService from '../services/contentService'
import Loading from '../components/Loading'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt  = n => (n == null ? '—' : Number(n).toLocaleString('es-CL'))
const fmtDate = s => {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner({ size = 28 }) {
  return (
    <div style={{
      width: size, height: size,
      border: `3px solid var(--g-border)`,
      borderTopColor: 'var(--g-lima)',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      display: 'inline-block',
    }} />
  )
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color = 'var(--g-lima)', sub }) {
  return (
    <div className="g-card" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
          <p style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 800, color: 'var(--g-dark)', lineHeight: 1.1 }}>{value}</p>
          {sub && <p style={{ margin: '2px 0 0', fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)' }}>{sub}</p>}
        </div>
        {icon && (
          <div style={{ width: 36, height: 36, borderRadius: 'var(--g-radius-md)', background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className={`bi ${icon}`} style={{ color, fontSize: 16 }}></i>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Video Card ────────────────────────────────────────────────────────────────
function VideoCard({ post }) {
  const engagement = (post.like_count || 0) + (post.comment_count || 0) + (post.share_count || 0)
  const er = post.view_count > 0 ? ((engagement / post.view_count) * 100).toFixed(2) : null

  return (
    <div className="g-card" style={{ overflow: 'hidden' }}>
      <div style={{ position: 'relative', aspectRatio: '9/16', background: '#111', overflow: 'hidden', maxHeight: 260 }}>
        {post.video_cover ? (
          <img src={post.video_cover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="bi bi-tiktok" style={{ fontSize: 32, opacity: 0.2, color: '#fff' }}></i>
          </div>
        )}
        <span style={{
          position: 'absolute', top: 8, left: 8,
          padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
          background: '#000', color: '#fff',
        }}>VIDEO</span>
        <span style={{
          position: 'absolute', bottom: 8, right: 8,
          padding: '2px 6px', borderRadius: 6, fontSize: 10,
          background: 'rgba(0,0,0,0.6)', color: '#fff',
        }}>
          {fmtDate(post.created_time)}
        </span>
      </div>

      <div style={{ padding: '10px 12px 0' }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--g-text-sec)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
          {post.caption || <em style={{ opacity: 0.4 }}>Sin descripción</em>}
        </p>
      </div>

      <div style={{ padding: '10px 12px 12px' }}>
        {[
          { icon: 'bi-eye-fill', label: 'Views', val: post.view_count },
          { icon: 'bi-heart-fill', label: 'Likes', val: post.like_count },
          { icon: 'bi-chat-fill', label: 'Comentarios', val: post.comment_count },
          { icon: 'bi-share-fill', label: 'Compartidos', val: post.share_count },
          er != null ? { icon: 'bi-graph-up-arrow', label: 'ER', val: `${er}%`, raw: true } : null,
        ].filter(m => m && m.val != null && m.val !== '').map(m => (
          <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <i className={`bi ${m.icon}`} style={{ fontSize: 11, color: 'var(--g-text-muted)', flexShrink: 0 }}></i>
            <span style={{ fontSize: 11, color: 'var(--g-text-muted)' }}>{m.label}</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: 'var(--g-dark)' }}>
              {m.raw ? m.val : fmt(m.val)}
            </span>
          </div>
        ))}
      </div>

      {post.video_url && (
        <div style={{ borderTop: '1px solid var(--g-border)', padding: '8px 12px' }}>
          <a href={post.video_url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: 'var(--g-teal)', textDecoration: 'none', fontWeight: 600 }}>
            <i className="bi bi-box-arrow-up-right" style={{ marginRight: 4 }}></i>Ver en TikTok
          </a>
        </div>
      )}
    </div>
  )
}

// ── Account Selector ──────────────────────────────────────────────────────────
function AccountSelector({ accounts, selected, onChange }) {
  return (
    <select
      value={selected}
      onChange={e => onChange(e.target.value)}
      style={{ padding: '6px 12px', borderRadius: 'var(--g-radius-sm)', border: '1px solid var(--g-border)', background: 'var(--g-surface)', color: 'var(--g-text)', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
    >
      {accounts.map(a => (
        <option key={a.id} value={a.id}>@{a.username}</option>
      ))}
    </select>
  )
}

// ── Cuenta Tab (perfil + KPIs + videos) ──────────────────────────────────────
function CuentaTab({ oauthAccount, trackedAccountId }) {
  const [posts, setPosts]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [days, setDays]       = useState(30)
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState(null)

  const cargarPosts = useCallback(() => {
    if (!trackedAccountId) { setPosts([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    contentService.getTikTokAccountPosts(trackedAccountId, days)
      .then(({ data }) => {
        // Un video puede tener varios snapshots (cada 10 min) — nos quedamos con el más reciente por video_id
        const byVideo = new Map()
        for (const p of (data.posts || [])) {
          const prev = byVideo.get(p.video_id)
          if (!prev || new Date(p.snapshot_timestamp) > new Date(prev.snapshot_timestamp)) {
            byVideo.set(p.video_id, p)
          }
        }
        setPosts(Array.from(byVideo.values()).sort((a, b) => new Date(b.created_time || b.snapshot_timestamp) - new Date(a.created_time || a.snapshot_timestamp)))
      })
      .catch(e => setError(e?.response?.data?.error || 'Error al cargar videos'))
      .finally(() => setLoading(false))
  }, [trackedAccountId, days])

  useEffect(() => { cargarPosts() }, [cargarPosts])

  const handleSync = async () => {
    if (!trackedAccountId) return
    setSyncing(true)
    setSyncMsg(null)
    try {
      const { data } = await contentService.fetchTikTokAccountData(trackedAccountId)
      setSyncMsg({ ok: true, text: `✓ ${data.videos_updated ?? 0} videos actualizados` })
      cargarPosts()
    } catch (e) {
      setSyncMsg({ ok: false, text: e?.response?.data?.error || 'Error al sincronizar' })
    } finally {
      setSyncing(false)
    }
  }

  const totalLikes    = posts.reduce((s, p) => s + (p.like_count || 0), 0)
  const totalComments = posts.reduce((s, p) => s + (p.comment_count || 0), 0)
  const totalShares   = posts.reduce((s, p) => s + (p.share_count || 0), 0)
  const totalViews    = posts.reduce((s, p) => s + (p.view_count || 0), 0)
  const totalEngagement = totalLikes + totalComments + totalShares
  const avgEr = posts.length && totalViews > 0
    ? ((totalEngagement / totalViews) * 100).toFixed(2)
    : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Botón sincronizar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          onClick={handleSync}
          disabled={syncing || !trackedAccountId}
          style={{
            padding: '10px 24px',
            borderRadius: 'var(--g-radius-md)',
            border: 'none',
            background: syncing ? 'var(--g-border)' : '#000',
            color: syncing ? 'var(--g-text-muted)' : '#fff',
            fontWeight: 800,
            fontSize: 14,
            cursor: syncing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            letterSpacing: '0.05em',
          }}
        >
          {syncing
            ? <><Spinner size={14} /> Sincronizando...</>
            : <><i className="bi bi-arrow-clockwise" /> SINCRONIZAR AHORA</>
          }
        </button>
        {syncMsg && (
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: syncMsg.ok ? '#16a34a' : '#dc2626',
            background: syncMsg.ok ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${syncMsg.ok ? '#86efac' : '#fca5a5'}`,
            borderRadius: 'var(--g-radius-sm)',
            padding: '6px 12px',
          }}>
            {syncMsg.text}
          </span>
        )}
      </div>

      {/* Perfil header */}
      <div className="g-card">
        <div className="g-card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {oauthAccount.avatar_url ? (
            <img src={oauthAccount.avatar_url} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              onError={e => { e.target.style.display = 'none' }} />
          ) : (
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="bi bi-tiktok" style={{ color: '#fff', fontSize: 28 }}></i>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 'var(--g-font-size-xl)', color: 'var(--g-dark)' }}>@{oauthAccount.username}</p>
            {oauthAccount.display_name && <p style={{ margin: '2px 0 0', fontSize: 'var(--g-font-size-sm)', color: 'var(--g-text-muted)' }}>{oauthAccount.display_name}</p>}
          </div>
          <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
            {[
              { label: 'Seguidores', val: fmt(oauthAccount.followers_count) },
              { label: 'Siguiendo', val: fmt(oauthAccount.following_count) },
              { label: 'Me gusta', val: fmt(oauthAccount.likes_count) },
              { label: 'Videos', val: fmt(oauthAccount.video_count) },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: 800, fontSize: 18, color: 'var(--g-dark)' }}>{s.val}</p>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--g-text-muted)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!trackedAccountId ? (
        <div className="g-card">
          <div className="g-empty">
            <i className="bi bi-hourglass-split g-empty-icon"></i>
            <p className="g-empty-title">Aún sin datos trackeados</p>
            <p className="g-empty-desc">El sistema recolecta métricas de videos automáticamente cada 10 minutos tras conectar la cuenta. Vuelve a intentar en unos minutos.</p>
          </div>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div>
            <p className="g-section-title" style={{ margin: '0 0 12px' }}>Métricas de la cuenta</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
              {[
                { label: 'Videos trackeados', val: fmt(posts.length), icon: 'bi-camera-reels-fill', color: '#000' },
                { label: 'Vistas totales', val: fmt(totalViews), icon: 'bi-eye-fill', color: '#3b82f6' },
                { label: 'Likes', val: fmt(totalLikes), icon: 'bi-heart-fill', color: '#ef4444' },
                { label: 'Comentarios', val: fmt(totalComments), icon: 'bi-chat-fill', color: '#6366f1' },
                { label: 'Compartidos', val: fmt(totalShares), icon: 'bi-share-fill', color: '#14b8a6' },
                { label: 'Engagement total', val: fmt(totalEngagement), icon: 'bi-hand-thumbs-up-fill', color: 'var(--g-lima)' },
                { label: 'ER promedio', val: avgEr != null ? `${avgEr}%` : '—', icon: 'bi-graph-up-arrow', color: '#10b981' },
              ].map(m => (
                <StatCard key={m.label} label={m.label} value={m.val} icon={m.icon} color={m.color} />
              ))}
            </div>
          </div>

          {/* Videos */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <p className="g-section-title" style={{ margin: 0 }}>Últimos {posts.length} videos</p>
              <select
                value={days}
                onChange={e => setDays(Number(e.target.value))}
                disabled={loading}
                style={{ padding: '4px 10px', borderRadius: 'var(--g-radius-sm)', border: '1px solid var(--g-border)', background: 'var(--g-surface)', color: 'var(--g-text)', fontSize: 13, cursor: 'pointer' }}
              >
                {[7, 15, 30, 60, 90].map(n => (
                  <option key={n} value={n}>Últimos {n} días</option>
                ))}
              </select>
            </div>

            {loading ? (
              <Loading fullScreen={false} mensaje="Cargando" />
            ) : error ? (
              <div style={{ padding: 24, color: '#dc2626', background: '#fef2f2', borderRadius: 'var(--g-radius-md)', border: '1px solid #fca5a5' }}>
                <i className="bi bi-exclamation-circle-fill" style={{ marginRight: 8 }}></i>{error}
              </div>
            ) : posts.length === 0 ? (
              <div className="g-card">
                <div className="g-empty">
                  <i className="bi bi-camera-reels g-empty-icon"></i>
                  <p className="g-empty-title">Sin videos trackeados en este período</p>
                  <p className="g-empty-desc">Prueba sincronizar la cuenta o ampliar el rango de días.</p>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                {posts.map(p => <VideoCard key={p.video_id} post={p} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TikTokAnalyticsPage() {
  const [oauthAccounts, setOauthAccounts]   = useState([])
  const [trackedAccounts, setTrackedAccounts] = useState([])
  const [loadingEstado, setLoadingEstado]   = useState(true)
  const [sel, setSel]                       = useState('')

  useEffect(() => {
    setLoadingEstado(true)
    Promise.all([
      contentService.getMyTikTokAccount().catch(() => ({ data: null })),
      contentService.getTrackedTikTokAccounts().catch(() => ({ data: [] })),
    ]).then(([{ data: my }, { data: tracked }]) => {
      let accounts = []
      if (my && Array.isArray(my.accounts)) accounts = my.accounts
      else if (my && my.id) accounts = [my]
      setOauthAccounts(accounts)
      setTrackedAccounts(Array.isArray(tracked) ? tracked : (tracked.results || []))
      if (accounts[0]) setSel(String(accounts[0].id))
    }).finally(() => setLoadingEstado(false))
  }, [])

  const selectedOauth = oauthAccounts.find(a => String(a.id) === String(sel))
  const matchedTracked = selectedOauth
    ? trackedAccounts.find(t => t.username === selectedOauth.username)
    : null

  return (
    <div className="g-page">
      <div className="g-page-header">
        <div>
          <h1 className="g-page-title">
            <i className="bi bi-tiktok" style={{ marginRight: 10, color: 'var(--g-lima)' }}></i>
            Analytics TikTok
          </h1>
          <p className="g-page-subtitle">Métricas completas de tus cuentas TikTok conectadas.</p>
        </div>
        {oauthAccounts.length > 1 && (
          <AccountSelector accounts={oauthAccounts} selected={sel} onChange={setSel} />
        )}
      </div>

      {loadingEstado ? (
        <Loading fullScreen={false} mensaje="Cargando" />
      ) : oauthAccounts.length === 0 ? (
        <div className="g-card">
          <div className="g-empty">
            <i className="bi bi-plug-fill g-empty-icon"></i>
            <p className="g-empty-title">No hay cuenta de TikTok conectada</p>
            <p className="g-empty-desc">Ve a Configuración → Cuentas TikTok para vincular tu cuenta.</p>
            <a href="/configuracion/cuentas-conectadas-tiktok" className="g-btn g-btn-primary">
              <i className="bi bi-plug-fill"></i>Conectar cuenta
            </a>
          </div>
        </div>
      ) : (
        <CuentaTab oauthAccount={selectedOauth} trackedAccountId={matchedTracked?.id} />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}
